import { useState } from 'react';
import { Download, FileText } from 'lucide-react';
import { Input } from '@/components/input';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { SkeletonList } from '@/components/skeleton';
import { DataList, DataListRow } from '@/components/data-list';
import { StatusPill } from '@/components/status-dot';
import { usePortalDocuments } from '@/lib/portal-documents';
import { formatDate, formatStatus } from '@/lib/format';
import type { DocumentData } from '@/types/api';

function formatBytes(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function PortalDocuments() {
  const [q, setQ] = useState('');
  const { data, isLoading } = usePortalDocuments({ q: q || undefined });
  const rows = data?.data;

  return (
    <div className="space-y-5">
      <PageHeader
        icon={FileText}
        title="Documents"
        subtitle="Contracts, agreements, and files the Mehrutherm team has shared with you."
      />

      <Input
        placeholder="Search documents…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="max-w-xs"
      />

      {isLoading ? (
        <SkeletonList rows={4} />
      ) : !rows || rows.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No documents yet"
          description="Files shared by Mehrutherm — contracts, runbooks, licenses — will appear here."
        />
      ) : (
        <DataList>
          {rows.map((doc) => (
            <DocumentRow key={doc.id} doc={doc} />
          ))}
        </DataList>
      )}
    </div>
  );
}

function DocumentRow({ doc }: { doc: DocumentData }) {
  const expiringSoon =
    doc.days_until_expiry != null && doc.days_until_expiry >= 0 && doc.days_until_expiry <= 30;
  const expired = doc.days_until_expiry != null && doc.days_until_expiry < 0;
  const meta = [formatStatus(doc.category), formatBytes(doc.file_size)].filter(Boolean).join(' · ');

  return (
    <DataListRow interactive={false}>
      <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-[var(--color-surface-muted)] text-[var(--color-foreground-muted)]">
        <FileText className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-foreground">{doc.title}</div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
          <span className="truncate">{meta}</span>
          {doc.expires_at ? (
            <>
              <span aria-hidden>·</span>
              <span>{expired ? 'Expired' : 'Expires'} {formatDate(doc.expires_at)}</span>
            </>
          ) : null}
        </div>
      </div>
      {expired ? (
        <StatusPill tone="danger">Expired</StatusPill>
      ) : expiringSoon ? (
        <StatusPill tone="warning">Expiring soon</StatusPill>
      ) : null}
      {doc.file_url ? (
        <a
          href={doc.file_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-[var(--color-foreground-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-foreground)]"
          aria-label={`Download ${doc.title}`}
        >
          <Download className="size-4" />
        </a>
      ) : null}
    </DataListRow>
  );
}
